import React, { useState, useEffect, useRef } from 'react';
import './Fedstryke.css';

function Fedstryke() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState('');
  const [modalAlt, setModalAlt] = useState('');
  const closeButtonRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const faqItems = [
    {
      question: 'Do I need every field?',
      answer: 'No. One field is enough to run a high-quality search.'
    },
    {
      question: 'How are results sorted?',
      answer: 'By Contract Fiscal Year, newest first.'
    },
    {
      question: 'What does Export to Excel include?',
      answer: "Exactly what’s on screen after your filters and sorting—nothing extra, nothing missing."
    },
    {
      question: 'Can I use open-ended dates?',
      answer: 'Yes. Leave start or end blank to keep the range open.'
    },
    {
      question: 'Can I search PSC by name or code?',
      answer: 'Yes—type a code (e.g., D399) or the title and select.'
    }
  ];

  const openModal = (imageSrc, imageAlt) => {
    lastFocusedRef.current = document.activeElement;
    setModalImage(imageSrc);
    setModalAlt(imageAlt);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent background scroll
    // focus will be moved to the close button after render
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalImage('');
    setModalAlt('');
    document.body.style.overflow = 'unset'; // Restore background scroll
    // restore focus to the element that opened the modal
    if (lastFocusedRef.current && typeof lastFocusedRef.current.focus === 'function') {
      try { lastFocusedRef.current.focus(); } catch (e) { /* ignore */ }
    }
  };

  const handleModalOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isModalOpen]);

  // focus the close button when modal opens
  useEffect(() => {
    if (isModalOpen && closeButtonRef.current) {
      // small timeout to ensure element is mounted
      setTimeout(() => {
        try { closeButtonRef.current.focus(); } catch (e) { /* ignore */ }
      }, 0);
    }
  }, [isModalOpen]);

  return (
    <div className="vitro-page">
      <section className="hero-section">
        <div className="hero-content">
          <h1>FedStryke™</h1>
          <p>Find, verify, and export federal contract information fast. Zero noise. Laser-focused search by keyword, company, PSC, fiscal year, and dates.</p>
          <div className="cta-buttons">
            <a href="https://govexai.com/" target="_blank" rel="noopener noreferrer" className="cta-button">Try the Live Search at GovExAI</a>
            <a href="/contact" className="cta-button">Book a 20‑min Fit Check</a>
          </div>
        </div>
      </section>

      <section className="why-section">
        <h2>Why FedStryke™</h2>
        <div className="columns-container">
          <div className="column">
            <span className="icon" aria-label="Speed">⚡</span>
            <h3>Speed to useful</h3>
            <p>Results prioritize newest Contract Fiscal Years first so you see the freshest opportunities.</p>
          </div>
          <div className="column">
            <span className="icon" aria-label="Quality">🎯</span>
            <h3>No blank fishing</h3>
            <p>At least one field required by design—higher-quality searches and fewer empty-result hunts.</p>
          </div>
          <div className="column">
            <span className="icon" aria-label="Export">📤</span>
            <h3>Instant handoff</h3>
            <p>One-click export to Excel of exactly what you see—filters and sort preserved for a frictionless handoff.</p>
          </div>
        </div>
      </section>

      <section className="features-section">
        <h2>What it does (in one minute)</h2>
        <div className="features-container-vitro">
          <div className="feature-card">
            <h3>Fast search</h3>
            <p className="summary">Enter one field (keyword or company) and get sorted results by fiscal year, newest first.</p>
          </div>
          <div className="feature-card">
            <h3>Detail on demand</h3>
            <p className="summary">Expand any row for contractor, obligated amount, contract number, dates, NAICS/PSC, agency, and place of performance.</p>
          </div>
          <div className="feature-card">
            <h3>Deterministic export</h3>
            <p className="summary">Download precisely the filtered and sorted table you’re viewing for sharing or analysis.</p>
          </div>
        </div>
      </section>

      <section className="compliance-section">
        <h2>Core capabilities</h2>
        <div className="compliance-container">
          <div className="compliance-column">
            <h3>Targeted search</h3>
            <ul>
              <li>Keyword, Company Name, PSC (code or title), Product/Service Description</li>
              <li>Fiscal Year, Date Signed (range), Award Completion (range)</li>
            </ul>
          </div>
          <div className="compliance-column">
            <h3>Usability</h3>
            <ul>
              <li>Enter-to-search, Clear-all, sticky filters, visible result count</li>
              <li>Pro tip: Start broad with 1–2 fields; add filters only if necessary</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="features-section">
        <h2>FAQs</h2>
        <div className="faq-list" style={{maxWidth: '900px', margin: '0 auto'}}>
          {faqItems.map((item, idx) => (
            <div key={idx} className={`faq-row ${openFaqIndex === idx ? 'open' : ''}`}>
              <button
                className="faq-question"
                onClick={() => toggleFaq(idx)}
                aria-expanded={openFaqIndex === idx}
                aria-controls={`faq-${idx}`}
              >
                <span>{item.question}</span>
                <span className="faq-toggle" aria-hidden="true">{openFaqIndex === idx ? '−' : '+'}</span>
              </button>
              <div
                id={`faq-${idx}`}
                className="faq-answer"
                role="region"
                aria-hidden={openFaqIndex !== idx}
              >
                <div className="faq-answer-inner">{item.answer}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="features-section screenshots-section">
        <h2>Screenshots</h2>
        <div className="interface-images">
          <div className="screenshot-container">
            <img
              src="/images/fedstryke-1.png"
              alt="FedStryke — search UI"
              className="screenshot-image"
              onClick={() => openModal('/images/fedstryke-1.png', 'FedStryke — Search UI')}
            />
            <p className="image-caption">Search UI: keyword + filters</p>
          </div>

          <div className="screenshot-container">
            <img
              src="/images/fedstryke-2.png"
              alt="FedStryke — expanded row details"
              className="screenshot-image"
              onClick={() => openModal('/images/fedstryke-2.png', 'FedStryke — Expanded Row Details')}
            />
            <p className="image-caption">Expanded row: contract details</p>
          </div>

          <div className="screenshot-container">
            <img
              src="/images/fedstryke-3.png"
              alt="FedStryke — export view"
              className="screenshot-image"
              onClick={() => openModal('/images/fedstryke-3.png', 'FedStryke — Export View')}
            />
            <p className="image-caption">Export: Excel-ready dataset</p>
          </div>
        </div>
      </section>

      {/* Lightbox modal: shows selected screenshot full-screen */}
      {/* Modal state and handlers are defined below via hooks */}
      {isModalOpen && (
        <div className="popup-overlay" onClick={handleModalOverlayClick} role="dialog" aria-modal="true">
          <div className={'popup-content' + (modalImage ? ' popup-content--image' : '')} onClick={(e) => e.stopPropagation()}>
            <button ref={closeButtonRef} className="popup-close" onClick={closeModal} aria-label="Close">×</button>
            <div style={{textAlign: 'center'}}>
              <img src={modalImage} alt={modalAlt} style={{maxWidth: '100%', height: 'auto', borderRadius: 8}} />
              {modalAlt && <p style={{marginTop: '0.75rem', color: '#334155'}}>{modalAlt}</p>}
            </div>
          </div>
        </div>
      )}

      <section className="cta-section-fedstryke">
        <h2>Ready to find the signal?</h2>
        <a href="https://govexai.com/" target="_blank" rel="noopener noreferrer" className="cta-button-fedstryke">Try the Live Search at GovEXAI</a>
      </section>
    </div>
  );
}

// If FedStryke™ is the discovery and targeting intelligence for federal opportunities,
// and GovExAI™ is the automated proposal generation and submission engine, then together under F3Iai™,
// you now own the entire contractor intelligence pipeline — from market awareness to award submission.

export default Fedstryke;
